import React, { useCallback, useEffect, useState } from 'react';
import { UserCog, Plus, Trash2, Save, Loader2, Star } from 'lucide-react';
import { api, errorText } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

// ── Types (mirror /api/master-profile) ───────────────────────────────────────
interface Role {
  role: string; company: string; portfolio_descriptor?: string;
  start_date?: string; end_date?: string; is_founder?: boolean; is_earlier?: boolean; bullets: string[];
}
interface Education { degree: string; field: string; school: string; year: string; honors: string }
interface SectorBridge { target_sector: string; bridge_line: string }
interface Profile {
  contact: { full_name: string; email: string; phone: string; location: string };
  eligibility_statement: string;
  location_stance: string;
  clearance: string | null;
  roles: Role[];
  metrics: string[];
  education: Education[];
  certifications: string[];
  sector_bridges: SectorBridge[];
}

const EMPTY: Profile = {
  contact: { full_name: '', email: '', phone: '', location: '' },
  eligibility_statement: '', location_stance: 'remote_hybrid', clearance: '',
  roles: [], metrics: [], education: [], certifications: [], sector_bridges: [],
};

const input = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500';
const label = 'block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1';
const card = 'bg-white dark:bg-navy-800 rounded-2xl border border-gray-200 dark:border-slate-800 p-5 space-y-4';

const MasterProfile: React.FC = () => {
  const { toast } = useToast();
  const [p, setP] = useState<Profile>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<{ profile: Profile | null }>('/api/master-profile', { retry: 2 });
      if (data.profile) setP({ ...EMPTY, ...data.profile, clearance: data.profile.clearance ?? '' });
    } catch (e) {
      toast.error(errorText(e, 'Failed to load profile'));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/master-profile', p);
      toast.success('Master Profile saved');
    } catch (e) {
      toast.error(errorText(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  // Helpers to mutate array sections immutably.
  const setRole = (i: number, patch: Partial<Role>) => setP((s) => ({ ...s, roles: s.roles.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const setEdu = (i: number, patch: Partial<Education>) => setP((s) => ({ ...s, education: s.education.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const setBridge = (i: number, patch: Partial<SectorBridge>) => setP((s) => ({ ...s, sector_bridges: s.sector_bridges.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));

  if (loading) {
    return <div className="max-w-4xl mx-auto px-4 py-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-primary-500/10 text-primary-600 dark:text-primary-400"><UserCog className="w-6 h-6" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Master Profile</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Your verified source of truth — generation grounds in this. The Founder/CTO role always leads.</p>
          </div>
        </div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>

      {/* Contact */}
      <section className={card}>
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Contact</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={label}>Full name</label><input className={input} value={p.contact.full_name} onChange={(e) => setP({ ...p, contact: { ...p.contact, full_name: e.target.value } })} /></div>
          <div><label className={label}>Email</label><input className={input} value={p.contact.email} onChange={(e) => setP({ ...p, contact: { ...p.contact, email: e.target.value } })} /></div>
          <div><label className={label}>Phone</label><input className={input} value={p.contact.phone} onChange={(e) => setP({ ...p, contact: { ...p.contact, phone: e.target.value } })} /></div>
          <div><label className={label}>Location</label><input className={input} value={p.contact.location} onChange={(e) => setP({ ...p, contact: { ...p.contact, location: e.target.value } })} /></div>
        </div>
      </section>

      {/* Eligibility / location */}
      <section className={card}>
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Eligibility &amp; location</h2>
        <div><label className={label}>Eligibility statement</label><input className={input} value={p.eligibility_statement} onChange={(e) => setP({ ...p, eligibility_statement: e.target.value })} placeholder="e.g. Canadian citizen with full right to work in Canada" /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={label}>Location stance</label>
            <select className={input} value={p.location_stance} onChange={(e) => setP({ ...p, location_stance: e.target.value })}>
              <option value="remote_hybrid">Remote / hybrid</option>
              <option value="relocate">Open to relocation</option>
              <option value="onsite">On-site</option>
            </select>
          </div>
          <div><label className={label}>Clearance (optional)</label><input className={input} value={p.clearance ?? ''} onChange={(e) => setP({ ...p, clearance: e.target.value })} placeholder="surfaced only when the JD asks for it" /></div>
        </div>
      </section>

      {/* Roles */}
      <section className={card}>
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Career history (tick the Founder/CTO role — it always leads)</h2>
          <button onClick={() => setP({ ...p, roles: [...p.roles, { role: '', company: '', bullets: [] }] })} className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"><Plus className="w-3.5 h-3.5" /> Add role</button>
        </div>
        {p.roles.length === 0 && <p className="text-xs text-slate-400">No roles yet.</p>}
        {p.roles.map((r, i) => (
          <div key={i} className="rounded-xl border border-slate-100 dark:border-slate-800 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-1 font-medium text-amber-600"><input type="checkbox" checked={!!r.is_founder} onChange={(e) => setRole(i, { is_founder: e.target.checked })} /><Star className="w-3 h-3" /> Founder/CTO</label>
                <label className="inline-flex items-center gap-1 text-slate-500"><input type="checkbox" checked={!!r.is_earlier} onChange={(e) => setRole(i, { is_earlier: e.target.checked })} /> Earlier experience (compressed)</label>
              </div>
              <button onClick={() => setP({ ...p, roles: p.roles.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <input className={input} placeholder="Role" value={r.role} onChange={(e) => setRole(i, { role: e.target.value })} />
              <input className={input} placeholder="Company" value={r.company} onChange={(e) => setRole(i, { company: e.target.value })} />
              <input className={input} placeholder="Start (e.g. Mar 2020)" value={r.start_date ?? ''} onChange={(e) => setRole(i, { start_date: e.target.value })} />
              <input className={input} placeholder="End (e.g. Present)" value={r.end_date ?? ''} onChange={(e) => setRole(i, { end_date: e.target.value })} />
            </div>
            {r.is_founder && <input className={input} placeholder="Portfolio descriptor (one line)" value={r.portfolio_descriptor ?? ''} onChange={(e) => setRole(i, { portfolio_descriptor: e.target.value })} />}
            {!r.is_earlier && (
              <div>
                <label className={label}>Bullets (one per line)</label>
                <textarea className={input} rows={4} value={r.bullets.join('\n')} onChange={(e) => setRole(i, { bullets: e.target.value.split('\n') })} />
              </div>
            )}
          </div>
        ))}
      </section>

      {/* Metrics */}
      <section className={card}>
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Reusable metrics (one per line)</h2>
        <textarea className={input} rows={6} value={p.metrics.join('\n')} onChange={(e) => setP({ ...p, metrics: e.target.value.split('\n') })} placeholder={'e.g. Cut cloud spend by 32% (~$2.1M/yr)\nSupported more than $1.5B in revenue'} />
      </section>

      {/* Education */}
      <section className={card}>
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Education</h2>
          <button onClick={() => setP({ ...p, education: [...p.education, { degree: '', field: '', school: '', year: '', honors: '' }] })} className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"><Plus className="w-3.5 h-3.5" /> Add</button>
        </div>
        {p.education.map((e, i) => (
          <div key={i} className="grid sm:grid-cols-5 gap-2 items-center">
            <input className={input} placeholder="Degree" value={e.degree} onChange={(ev) => setEdu(i, { degree: ev.target.value })} />
            <input className={input} placeholder="Field" value={e.field} onChange={(ev) => setEdu(i, { field: ev.target.value })} />
            <input className={input} placeholder="School" value={e.school} onChange={(ev) => setEdu(i, { school: ev.target.value })} />
            <input className={input} placeholder="Year" value={e.year} onChange={(ev) => setEdu(i, { year: ev.target.value })} />
            <div className="flex gap-1"><input className={input} placeholder="Honors" value={e.honors} onChange={(ev) => setEdu(i, { honors: ev.target.value })} /><button onClick={() => setP({ ...p, education: p.education.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></div>
          </div>
        ))}
      </section>

      {/* Certifications */}
      <section className={card}>
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Certifications (one per line)</h2>
        <textarea className={input} rows={5} value={p.certifications.join('\n')} onChange={(e) => setP({ ...p, certifications: e.target.value.split('\n') })} />
      </section>

      {/* Sector bridges */}
      <section className={card}>
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Sector bridges (one per target sector)</h2>
          <button onClick={() => setP({ ...p, sector_bridges: [...p.sector_bridges, { target_sector: '', bridge_line: '' }] })} className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"><Plus className="w-3.5 h-3.5" /> Add</button>
        </div>
        {p.sector_bridges.map((b, i) => (
          <div key={i} className="space-y-2 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
            <div className="flex gap-2 items-center">
              <input className={input} placeholder="Target sector (e.g. insurance / public-sector regulator)" value={b.target_sector} onChange={(e) => setBridge(i, { target_sector: e.target.value })} />
              <button onClick={() => setP({ ...p, sector_bridges: p.sector_bridges.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <textarea className={input} rows={2} placeholder="One honest sentence bridging your background to that sector" value={b.bridge_line} onChange={(e) => setBridge(i, { bridge_line: e.target.value })} />
          </div>
        ))}
      </section>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Master Profile
        </button>
      </div>
    </div>
  );
};

export default MasterProfile;
